/**
 * Calendar days and wall-clock hours in a named time zone. A "date key" is a
 * local calendar day as YYYY-MM-DD. Plain UTC arithmetic on date keys is
 * safe: a day is a day, whatever the zone's offset does.
 */

const DAY_MS = 24 * 60 * 60 * 1000

export type ZonedParts = Readonly<{
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}>

export const zonedParts = (date: Date, timeZone: string): ZonedParts => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date)
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value)
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second"),
  }
}

const pad = (value: number, width = 2) => String(value).padStart(width, "0")

const keyOf = (utcMidnight: number) => {
  const date = new Date(utcMidnight)
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

const midnightOf = (dateKey: string) => {
  const [year, month, day] = dateKey.split("-").map(Number)
  return Date.UTC(year!, month! - 1, day!)
}

/** The local calendar day an instant falls on. */
export const zonedDateKey = (date: Date, timeZone: string): string => {
  const local = zonedParts(date, timeZone)
  return keyOf(Date.UTC(local.year, local.month - 1, local.day))
}

export const addDaysToDateKey = (dateKey: string, days: number): string =>
  keyOf(midnightOf(dateKey) + days * DAY_MS)

/** Whole days from one date key to another. */
export const daysBetweenDateKeys = (from: string, to: string): number =>
  Math.round((midnightOf(to) - midnightOf(from)) / DAY_MS)

/** 0 is Sunday, 6 is Saturday. */
export const weekdayOfDateKey = (dateKey: string): number =>
  new Date(midnightOf(dateKey)).getUTCDay()

/** The day itself on a weekday; a Saturday or Sunday moves to Monday. */
export const weekdayOnOrAfter = (dateKey: string): string => {
  const weekday = weekdayOfDateKey(dateKey)
  if (weekday === 6) return addDaysToDateKey(dateKey, 2)
  if (weekday === 0) return addDaysToDateKey(dateKey, 1)
  return dateKey
}

/**
 * The due date a form opens on: `days` from today, moved to Monday when it
 * lands on a weekend. It only ever moves forward, so it is never before
 * today.
 */
export const defaultDueDateKey = (todayKey: string, days: number): string =>
  weekdayOnOrAfter(addDaysToDateKey(todayKey, Math.max(0, days)))

/**
 * The instant a local day reaches `hour` o'clock. The offset is checked
 * again after the first guess, so a day that changes clocks still lands on
 * the hour. When clocks jump over the hour, the next time that exists that
 * day is used.
 */
export const zonedHourOnDate = (
  dateKey: string,
  hour: number,
  timeZone: string
): Date => {
  const target = midnightOf(dateKey) + hour * 60 * 60 * 1000
  const observed = (candidate: number) => {
    const parts = zonedParts(new Date(candidate), timeZone)
    return Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute
    )
  }
  let candidate = target
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const seen = observed(candidate)
    if (seen === target) return new Date(candidate)
    candidate += target - seen
  }
  for (let attempt = 0; attempt < 180; attempt += 1) {
    if (observed(candidate) >= target) return new Date(candidate)
    candidate += 60_000
  }
  return new Date(candidate)
}

/** True when the runtime knows the IANA zone. */
export const isKnownTimeZone = (timeZone: string): boolean => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format(new Date(0))
    return true
  } catch {
    return false
  }
}
